import { useState } from 'react'
import {
    Button,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'

import {
    useListConnectInstallsQuery,
    useRevokeConnectInstallMutation,
    type ConnectInstall,
} from '@/apis/connectApi'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { ReportStatus, fmtDateTime } from '@/modules/reports/common'

const REASON: Record<string, string> = {
    uninstalled: 'app uninstalled',
    revoked_by_user: 'revoked here',
    replaced: 'reinstalled',
}

/** Connected integrations (YOM Connect installs) of a project, with revoke. */
export function IntegrationsCard({ projectId }: { projectId: string }) {
    const {
        data = [],
        isLoading,
        error,
    } = useListConnectInstallsQuery(projectId, {
        skip: !projectId,
    })
    const [revoke, { error: revokeError }] = useRevokeConnectInstallMutation()
    const [target, setTarget] = useState<ConnectInstall | null>(null)

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6">Connected integrations</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Store apps connected with YOM Connect. Each holds one publishable and one secret
                    key; revoking stops both immediately.
                </Typography>
                {revokeError && (
                    <Typography color="error" variant="body2">
                        {v2ErrorMessage(revokeError)}
                    </Typography>
                )}
                <ReportStatus
                    isLoading={isLoading}
                    error={error}
                    isEmpty={data.length === 0}
                    emptyTitle="No connected integrations"
                    emptyDescription="Install YOM from your store platform's app marketplace to connect it here."
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Platform</TableCell>
                                <TableCell>Store</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Connected</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.map(i => (
                                <TableRow
                                    key={i.id}
                                    sx={i.status === 'revoked' ? { opacity: 0.6 } : undefined}
                                >
                                    <TableCell sx={{ textTransform: 'capitalize' }}>
                                        {i.platform}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>
                                        {i.store}
                                    </TableCell>
                                    <TableCell>{i.sub_tenant ?? '—'}</TableCell>
                                    <TableCell>{fmtDateTime(i.created_at)}</TableCell>
                                    <TableCell>
                                        {i.status === 'active' ? (
                                            <Chip size="small" color="success" label="active" />
                                        ) : (
                                            <Chip
                                                size="small"
                                                label={`revoked${i.revoked_reason ? ` · ${REASON[i.revoked_reason] ?? i.revoked_reason}` : ''}`}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {i.status === 'active' && (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => setTarget(i)}
                                            >
                                                Revoke
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ReportStatus>
            </CardContent>
            <ConfirmDialog
                open={target !== null}
                title="Revoke this integration?"
                message={`${target?.platform ?? ''} (${target?.store ?? ''}) stops sending data now. Reconnect from the app to undo.`}
                confirmLabel="Revoke"
                confirmColor="error"
                onCancel={() => setTarget(null)}
                onConfirm={() => {
                    if (target) {
                        void revoke({ projectId, id: target.id })
                    }
                    setTarget(null)
                }}
            />
        </Card>
    )
}
